import React, { useState } from "react";
import {
  useClients,
  useCreateClient,
  useDeleteClient,
  useUpdateClient,
} from "../../hooks/useClients";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { Input, Textarea } from "../../components/ui/Input";
import { useNavigate } from "react-router";

interface ClientFormData {
  name: string;
  client_id?: string;
  description?: string;
}

export default function ClientsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ClientFormData>({
    name: "",
    client_id: "Aetna_Medicare",
    description: "",
  });
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useClients({
    page,
    page_size: 12,
    search: search || undefined,
  });
  const createMutation = useCreateClient();
  const updateMutation = useUpdateClient();
  const deleteMutation = useDeleteClient();

  const clients = data?.clients || [];
  const totalPages = data?.total_pages || 1;

  const openCreate = () => {
    setEditingId(null);
    setFormData({ name: "", client_id: "Aetna_Medicare", description: "" });
    setShowForm(true);
  };

  const openEdit = (client: any) => {
    navigate(`/clients/${client.id}/edit`);
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, data: formData });
    } else {
      await createMutation.mutateAsync(formData);
    }
    setShowForm(false);
    refetch();
  };

  const onDelete = async (id: number) => {
    await deleteMutation.mutateAsync(id);
    refetch();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-600 mt-1">
            Manage tenants for multi-tenant support
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Create Client
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center gap-3">
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or description"
          variant="default"
          className="flex-1"
          showLabel={false}
        />
        <button
          onClick={() => setPage(1)}
          className="px-3 py-2 bg-gray-100 rounded border"
        >
          Apply
        </button>
        <button
          onClick={() => {
            setSearch("");
            setPage(1);
          }}
          className="px-3 py-2 bg-gray-100 rounded border"
        >
          Clear
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      )}
      {isError && (
        <ErrorMessage
          message="Failed to load clients"
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client) => (
            <div key={client.id} className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{client.name}</h3>
                <span className="text-xs text-gray-500">#{client.id}</span>
              </div>
              <div className="text-sm text-gray-600 mb-2">
                Client ID: <span className="font-mono">{client.client_id}</span>
              </div>
              {client.description && (
                <div className="text-sm text-gray-700 mb-3">
                  {client.description}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => openEdit(client)}
                  className="px-3 py-1 text-sm bg-white border rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(client.id)}
                  className="px-3 py-1 text-sm bg-red-500 text-white rounded"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && !isError && (data?.total_pages || 0) > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-2 bg-white border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages}
            className="px-3 py-2 bg-white border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">
                {editingId ? "Edit Client" : "Create Client"}
              </h2>
              <form onSubmit={submitForm} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client ID
                  </label>
                  <input
                    type="text"
                    value={formData.client_id || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, client_id: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <Textarea
                    label="Description"
                    value={formData.description || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    variant="rounded-md"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 bg-gray-100 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded"
                  >
                    {editingId ? "Save Changes" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
